import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Formik} from 'formik';
import {useRouteMatch} from 'react-router-dom';
import Form_5View from './Form_5-view';
import {checkRequiredValues} from 'common/utils/form/checkRequiredValues';
import {normalizeFormValues} from 'common/utils/form/normalizeFormValues';
import {pathnames as P} from 'common/constants/pathnames';
import {fileTypeId as T} from '../../constants/fileTypeId';
import {propertyTypesGroups as PT} from '../../constants/propertyTypesGroups';
import {isFileUploaded} from '../../utils/isFileUploaded';
import SilentSubmitHandler from '../SilentSubmitHandler';
import ModalPreviewDocument from '../../../../common/components/ModalPreviewDocument';
import {
  buildingJointHomeOptions,
  houseJointHomeRegisteredOptions,
  landDirectionOptions,
} from '../../../../common/constants/options';
import {uploadingProgress} from '../../../../common/utils/form/uploadingProgress';
import {
  filterFileListByFileTypeId, getFileFolderID,
  getSharedAndPrivateFiles
} from '../../utils/fileHelpers';
import ButtonSubmitForm from '../ButtonSubmitForm';
import SpinnerContainer from '../../../../common/components/SpinnerContainer';

const fileTypeIds = {
  dedicatedAreaMap: T.DEDICATED_AREA_MAP,
};

const Form_5 = ({
                  submitStep,
                  stepData,
                  uploadFile,
                  getShumaStepStatus,
                  getStepData,
                  showGlobalErrorMessage,
                  shumaTypeID,
                  propertyTypeID,
                  getFileList,
                  filesListData
                }) => {
  const [reinit, setReinit] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showUploadingFilePopUp, setShowUploadingFilePopUp] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    fileList,
    cityName,
    streetName,
    houseNumber,
    buildingJointHome,
    landFront,
    landDirection,
    houseJointHomeRegistered,
    houseRegisteredAreaIncludeBalcony,
    houseRegisteredAreaIncludeStorage,
    housePermitAreaIncludeBalcony,
    housePermitAreaIncludeStorage,
    ...rest
  } = stepData;

  const [previousBuildingJointHome] = buildingJointHome ? buildingJointHomeOptions.filter(opt => opt.value === buildingJointHome) : [];
  const [previousHouseJointHomeRegistered] = houseJointHomeRegistered ? houseJointHomeRegisteredOptions.filter(opt => opt.value === houseJointHomeRegistered) : [];
  const [previousLandDirection] = landDirection ? landDirectionOptions.filter(opt => opt.value === landDirection) : [];

  const initialValues = {
    landFront: Boolean(landFront) || null,
    landDirection: (landDirection) ? previousLandDirection : null,
    buildingJointHome: (buildingJointHome) ? previousBuildingJointHome : null,
    houseRegisteredArea: '',
    houseJointHomeRegistered: (houseJointHomeRegistered) ? previousHouseJointHomeRegistered : null,
    housePermitArea: '',
    houseMainArea: '',
    houseServiceArea: '',
    housePermitAreaText: '',
    houseRegisteredAreaIncludeBalcony: Boolean(houseRegisteredAreaIncludeBalcony) || null,
    houseRegisteredAreaIncludeStorage: Boolean(houseRegisteredAreaIncludeStorage) || null,
    housePermitAreaIncludeBalcony: Boolean(housePermitAreaIncludeBalcony) || null,
    housePermitAreaIncludeStorage: Boolean(housePermitAreaIncludeStorage) || null,
    houseBalconyOpenRegisteredArea: '',
    houseBalconyClosedRegisteredArea: '',
    houseYardRegisteredArea: '',
    houseStorageRegisteredArea: '',
    houseRoofRegisteredArea: '',
    houseParkingRegisteredArea: '',
    houseBalconyOpenPermitArea: '',
    houseBalconyClosedPermitArea: '',
    houseYardPermitArea: '',
    houseStoragePermitArea: '',
    houseRoofPermitArea: '',
    houseParkingPermitArea: '',
    propertyAreaByRule9: '',
    propertyAreaByMunicipal: '',
    houseAreaAccordingTasritMeher: '',
    houseAreaAccordingHeskemShituf: '',
    houseAreaAccordingTasritTaba: '',
    houseAreaAccordingTasritHahira: '',
    houseAreaAccordingNesahRishum: '',
    landShetachText: '',
    registeredAreaAdditionalDescription: '',
    ...rest
  };

  const dedicatedAreaMapFileUploaded = isFileUploaded({fileList, fileTypeID: T.DEDICATED_AREA_MAP});

  const {params: {shumaId, formId: stepId}} = useRouteMatch(P.INSPECTION_AND_FORM_IDS);

  const validate = values => {
    let errors = {};

    const {
      buildingTenantsLicenseCount,
      buildingTenantsRegisteredCount,
      houseMainArea,
      houseServiceArea,
      housePermitArea,
      housePermitAreaText,
      houseBalconyOpenRegisteredArea,
      houseBalconyClosedRegisteredArea,
      houseYardRegisteredArea,
      houseStorageRegisteredArea,
      houseRoofRegisteredArea,
      houseParkingRegisteredArea,
      houseBalconyOpenPermitArea,
      houseBalconyClosedPermitArea,
      houseYardPermitArea,
      houseStoragePermitArea,
      houseRoofPermitArea,
      houseParkingPermitArea,
      propertyAreaByRule9,
      propertyAreaByMunicipal,
      landFront,
      apiExists,
      gisLink,
      houseAreaAccordingTasritMeher,
      houseAreaAccordingHeskemShituf,
      houseAreaAccordingTasritTaba,
      houseAreaAccordingTasritHahira,
      houseAreaAccordingNesahRishum,
      landShetachText,
      registeredAreaAdditionalDescription,
      ...rest
    } = values;

    if (shumaTypeID === 3 || shumaTypeID === 4) {
      errors = checkRequiredValues({landFront, ...rest}, {})
    } else {
      errors = checkRequiredValues(rest)
    }

    return errors;
  };

  const submitWithoutFormReinitialization = values => {
    const {
      landFront,
      landDirection,
      buildingJointHome,
      houseRegisteredArea,
      houseJointHomeRegistered,
      housePermitArea,
      houseMainArea,
      houseServiceArea,
      housePermitAreaText,
      houseRegisteredAreaIncludeBalcony,
      houseRegisteredAreaIncludeStorage,
      housePermitAreaIncludeBalcony,
      housePermitAreaIncludeStorage,
      houseBalconyOpenRegisteredArea,
      houseBalconyClosedRegisteredArea,
      houseYardRegisteredArea,
      houseStorageRegisteredArea,
      houseRoofRegisteredArea,
      houseParkingRegisteredArea,
      houseBalconyOpenPermitArea,
      houseBalconyClosedPermitArea,
      houseYardPermitArea,
      houseStoragePermitArea,
      houseRoofPermitArea,
      houseParkingPermitArea,
      propertyAreaByRule9,
      propertyAreaByMunicipal,
      houseAreaAccordingTasritMeher,
      houseAreaAccordingHeskemShituf,
      houseAreaAccordingTasritTaba,
      houseAreaAccordingTasritHahira,
      houseAreaAccordingNesahRishum,
      landShetachText,
      registeredAreaAdditionalDescription,
    } = normalizeFormValues(values);
    const data = {
      shumaID: shumaId,
      shumaTypeID,
      buildingJointHome,
      houseRegisteredArea,
      houseJointHomeRegistered,
      landFront,
      landDirection,
      housePermitArea,
      houseMainArea,
      houseServiceArea,
      housePermitAreaText,
      houseRegisteredAreaIncludeBalcony,
      houseRegisteredAreaIncludeStorage,
      housePermitAreaIncludeBalcony,
      housePermitAreaIncludeStorage,
      houseBalconyOpenRegisteredArea,
      houseBalconyClosedRegisteredArea,
      houseYardRegisteredArea,
      houseStorageRegisteredArea,
      houseRoofRegisteredArea,
      houseParkingRegisteredArea,
      houseBalconyOpenPermitArea,
      houseBalconyClosedPermitArea,
      houseYardPermitArea,
      houseStoragePermitArea,
      houseRoofPermitArea,
      houseParkingPermitArea,
      propertyAreaByRule9,
      propertyAreaByMunicipal,
      houseAreaAccordingTasritMeher,
      houseAreaAccordingHeskemShituf,
      houseAreaAccordingTasritTaba,
      houseAreaAccordingTasritHahira,
      houseAreaAccordingNesahRishum,
      landShetachText,
      registeredAreaAdditionalDescription,
    };

    setReinit(false);  //https://github.com/formium/formik/issues/2369
    return submitStep({step: 5, data})
      .then(() => setReinit(true))
  };

  const handleDataSubmit = (values, shumaId, dirty) => {
    if (dirty) {
      submitWithoutFormReinitialization(values)
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message));
    }
  };

  const handleSubmitForSharedFiles = formikProps => {
    Promise.resolve()
      .then(() => {
        submitWithoutFormReinitialization(formikProps.values)
      })
      .then(() => getShumaStepStatus({shumaId, resetValues: false}))
  };

  const handleFileUpload = formikProps => fileTypeId => closeDropdown => (file, payload) => {
    setShowUploadingFilePopUp(true);
    submitFiles(fileTypeId)(file, payload)
      .then(() => uploadingProgress(setProgress))
      .then(() => submitWithoutFormReinitialization(formikProps.values))
      .then(() => {
        if (closeDropdown) closeDropdown()
      })
      .then(() => getStepData({shumaId, stepId}))
      .then(() => getShumaStepStatus({shumaId, resetValues: false}))
      .then(() => setShowUploadingFilePopUp(false))
  };

  const submitFiles = fileTypeId => (fileToSave, payload) => {
    return uploadFile({shumaId, filename: fileToSave.name, fileTypeId, fileToSave}, payload);
  };

  useEffect(() => {getFileList({shumaId})}, []);

  const getFileSelectOptions = formikProps => {
    const filteredSharedFileID9 = filterFileListByFileTypeId(fileList, fileTypeIds.dedicatedAreaMap);
    const onSubmit = () => handleSubmitForSharedFiles(formikProps);

    return {
      dedicatedAreaMap: {
        type: getSharedAndPrivateFiles(filteredSharedFileID9),
        fileTypeID: fileTypeIds.dedicatedAreaMap,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.dedicatedAreaMap),
      },
    };
  };

  const handleDataSubmitForPreview = (values, shumaId, dirty) => {
    if (dirty) {
      submitWithoutFormReinitialization(values)
        .then(() => getStepData({shumaId, stepId}))
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message));
    }
  };

  const handleDataSubmitForSave = (values, shumaId, dirty) => {
    if (dirty) {
      setLoading(true);
      submitWithoutFormReinitialization(values)
        .then(() => getStepData({shumaId, stepId}))
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message))
        .finally(() => setLoading(false));
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={() => {
      }}
      enableReinitialize={reinit}
      validateOnChange={true}
      validateOnBlur={false}
    >
      {(formikProps) => (
        <>
          <SilentSubmitHandler
            onSubmit={handleDataSubmit}
            values={formikProps.values}
            shumaId={shumaId}
            dirty={formikProps.dirty}
          />
          <ButtonSubmitForm
            onClick={() => {
              handleDataSubmitForSave(
                formikProps.values,
                shumaId,
                formikProps.dirty
              )
            }}
            disabled={!formikProps.dirty}
          />
          <ModalPreviewDocument
            submitOnOpen={() => {
              handleDataSubmitForPreview(
                formikProps.values,
                shumaId,
                formikProps.dirty
              )
            }}
          />
          {loading ? <SpinnerContainer/> : (
            <Form_5View
              {...formikProps}
              prevPagePath={P.GET_FORM_BY_ID_AND_STEP(shumaId, 6)}
              nextPagePath={P.GET_FORM_BY_ID_AND_STEP(shumaId, 9)}
              options={{
                buildingJointHomeOptions,
                houseJointHomeRegisteredOptions,
                landDirectionOptions,
              }}
              fileUploaded={{
                dedicatedAreaMapFileUploaded,
              }}
              onFileSelected={handleFileUpload(formikProps)}
              fileTypeIds={fileTypeIds}
              fileList={fileList}
              showUploadingFilePopUp={showUploadingFilePopUp}
              progress={progress}
              stepData={stepData}
              shumaID={shumaId}
              stepID={stepId}
              shumaTypeID={shumaTypeID}
              fileSelectOptions={getFileSelectOptions(formikProps)}
              propertyType={{
                isRegular: PT.REGULAR.includes(propertyTypeID),
                isGroundOrLot: PT.GROUND_OR_LOT.includes(propertyTypeID),
                isLot: PT.LOT.includes(propertyTypeID),
                isGround: PT.GROUND.includes(propertyTypeID),
              }}
            />
          )}
        </>
      )}
    </Formik>
  );
};

Form_5.propTypes = {
  submitStep: PropTypes.func,
  uploadFile: PropTypes.func,
  stepData: PropTypes.object,
};

export default Form_5;
